import { PROGRAM_TAXONOMY } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/customerProgram'
import { ProviderProgramTaxonomy } from '@lyrahealth-inc/shared-app-logic/src/models/providers/Providers'

import { hasRole } from './utils'
import { ROLES } from '../constants/appConstants'

export const DIRECT_ACCESS_CAPACITY_KEY = 'DirectAccess'

const DIRECT_ACCESS_PROVIDER_ROLES = [
  ROLES.DA_COUPLES_THERAPIST,
  ROLES.DA_FAMILY_THERAPIST,
  ROLES.DA_INDIVIDUAL_THERAPIST,
  ROLES.DA_CHILD_THERAPIST,
  ROLES.DA_ADOLESCENT_THERAPIST,
  ROLES.ICAS_PROVIDER,
]

// DA Therapy, DA Meds, ICAS and customer-specific onsite taxonomies all track capacity under DirectAccess
const isDirectAccessTaxonomy = (taxonomy: ProviderProgramTaxonomy, isOnsite: boolean) => {
  const isTherapyOrMeds =
    taxonomy.treatment === PROGRAM_TAXONOMY.treatment.therapy ||
    taxonomy.treatment === PROGRAM_TAXONOMY.treatment.medicationManagement
  if (!isTherapyOrMeds) {
    return false
  }

  const isDirectAccessPartner =
    taxonomy.partner === PROGRAM_TAXONOMY.partner.directAccess || taxonomy.partner === PROGRAM_TAXONOMY.partner.icas
  const isOnsitePartner =
    isOnsite &&
    typeof taxonomy.partner === 'string' &&
    taxonomy.partner.trim().length > 0 &&
    taxonomy.partner !== PROGRAM_TAXONOMY.partner.blendedCare

  return isDirectAccessPartner || isOnsitePartner
}

type ShouldShowExpeditedBookingArgs = {
  programName: string
  programTaxonomies?: ProviderProgramTaxonomy[]
  providerRoles?: string[]
  providerOnsite?: boolean | string
  isSelfService?: boolean
  roleAllowsExpeditedBooking?: boolean
}

/**
 * Existing LCx rows keep their role-config behavior. DirectAccess is decided from
 * the provider's program evidence instead, because its capacity key is also used
 * by DA Therapy, DA Meds, ICAS, and customer-specific onsite DA programs.
 *
 * For older payloads without program taxonomies, only explicit DA therapist and
 * ICAS roles provide enough evidence to expose the DirectAccess control. Generic
 * provider and practice-provider roles are not DA eligibility signals.
 */
export const shouldShowExpeditedBooking = ({
  programName,
  programTaxonomies = [],
  providerRoles = [],
  providerOnsite = false,
  isSelfService = true,
  roleAllowsExpeditedBooking = false,
}: ShouldShowExpeditedBookingArgs) => {
  if (programName !== DIRECT_ACCESS_CAPACITY_KEY) {
    return roleAllowsExpeditedBooking
  }

  // Delegated editing reads the logged-in user's expedited config, not the selected provider's.
  if (!isSelfService) {
    return false
  }

  if (programTaxonomies.length > 0) {
    const isOnsite = providerOnsite === true || providerOnsite === 'true'
    return programTaxonomies.some((taxonomy) => isDirectAccessTaxonomy(taxonomy, isOnsite))
  }

  return hasRole(providerRoles, DIRECT_ACCESS_PROVIDER_ROLES)
}
